/**
 * SmoothTool - Boundary smoothing controls
 * 
 * Provides UI for selecting a region of the boundary and applying smoothing
 * with adjustable parameters (iterations, lambda).
 */

import React, { useState } from 'react'
import './SmoothTool.css'

interface SmoothToolProps {
  /** Selected region start index (-1 if not selected) */
  startIndex: number
  /** Selected region end index (-1 if not selected) */
  endIndex: number
  /** Total number of control points */
  totalPoints: number
  /** Callback to apply smoothing */
  onSmooth: (iterations: number, lambda: number, preview: boolean) => void
  /** Callback to cancel smoothing mode */
  onCancel: () => void
  /** Loading state */
  isLoading?: boolean
}

export const SmoothTool: React.FC<SmoothToolProps> = ({
  startIndex,
  endIndex,
  totalPoints,
  onSmooth,
  onCancel,
  isLoading = false
}) => {
  const [iterations, setIterations] = useState<number>(3)
  const [lambda, setLambda] = useState<number>(0.5)
  
  const hasStartPoint = startIndex >= 0
  const hasEndPoint = endIndex >= 0
  const hasValidRegion = hasStartPoint && hasEndPoint && startIndex !== endIndex

  const getRegionSize = (): number => {
    if (!hasValidRegion) return 0
    if (startIndex <= endIndex) {
      return endIndex - startIndex + 1
    }
    return (totalPoints - startIndex) + endIndex + 1
  }

  const regionSize = getRegionSize()

  const handlePreview = () => {
    if (!hasValidRegion) return
    onSmooth(iterations, lambda, true)
  }

  const handleApply = () => {
    if (!hasValidRegion) return
    onSmooth(iterations, lambda, false)
  }

  const getStatusText = (): string => {
    if (!hasStartPoint) return 'Click a control point to select start'
    if (!hasEndPoint) return 'Click a second control point to select end'
    if (hasValidRegion) return `Selected ${regionSize} points`
    return 'Select a region to smooth'
  }

  return (
    <div className="smooth-tool-panel">
      <div className="smooth-tool-header">
        <h3>Boundary Smoothing</h3>
        <button
          onClick={onCancel}
          className="smooth-tool-close"
          disabled={isLoading}
          title="Exit smooth mode"
        >
          ✕
        </button>
      </div>

      <div className="smooth-tool-body">
        <div className="region-selection-status">
          <div className={`status-indicator ${hasValidRegion ? 'valid' : 'pending'}`}>
            {getStatusText()}
          </div>
          
          {hasValidRegion && (
            <div className="region-info">
              <div className="region-info-row">
                <span className="region-label">Start:</span>
                <span className="region-value">{startIndex}</span>
              </div>
              <div className="region-info-row">
                <span className="region-label">End:</span>
                <span className="region-value">{endIndex}</span>
              </div>
              <div className="region-info-row">
                <span className="region-label">Region size:</span>
                <span className="region-value">{regionSize} points</span>
              </div>
            </div>
          )}
        </div>

        <div className="smooth-parameters">
          <div className="parameter-group">
            <label htmlFor="iterations-slider">
              Iterations
              <span className="parameter-value">{iterations}</span>
            </label>
            <input
              id="iterations-slider"
              type="range"
              min="1"
              max="50"
              step="1"
              value={iterations}
              onChange={(e) => setIterations(parseInt(e.target.value, 10))}
              disabled={isLoading}
              className="parameter-slider"
            />
            <div className="slider-labels">
              <span>1</span>
              <span>25</span>
              <span>50</span>
            </div>
          </div>

          <div className="parameter-group">
            <label htmlFor="lambda-slider">
              Smoothing Factor (λ)
              <span className="parameter-value">{lambda.toFixed(2)}</span>
            </label>
            <input
              id="lambda-slider"
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={lambda}
              onChange={(e) => setLambda(parseFloat(e.target.value))}
              disabled={isLoading}
              className="parameter-slider"
            />
            <div className="slider-labels">
              <span>0.0</span>
              <span>0.5</span>
              <span>1.0</span>
            </div>
          </div>
        </div>

        <div className="smooth-actions">
          <button
            onClick={handlePreview}
            disabled={!hasValidRegion || isLoading}
            className="smooth-preview-button"
            title="Preview smoothing result without saving"
          >
            {isLoading ? 'Previewing...' : 'Preview'}
          </button>
          <button
            onClick={handleApply}
            disabled={!hasValidRegion || isLoading}
            className="smooth-apply-button"
            title="Apply smoothing and save to undo stack"
          >
            {isLoading ? 'Applying...' : 'Apply'}
          </button>
        </div>

        <div className="smooth-help">
          <p className="help-item">Higher iterations = more smoothing</p>
          <p className="help-item">Higher λ = stronger smoothing effect</p>
          <p className="help-item">Preview shows result as dashed line</p>
        </div>
      </div>
    </div>
  )
}
